<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import Spinner from 'shared/components/Spinner.vue';

defineProps({
  handoffCount: {
    type: Number,
    default: 0,
  },
  unrespondedData: {
    type: Object,
    default: () => ({ count: 0, conversations: [] }),
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();
const route = useRoute();

const accountId = computed(() => route.params.accountId);

const conversationUrl = id =>
  `/app/accounts/${accountId.value}/conversations/${id}`;
</script>

<template>
  <div
    class="flex flex-col rounded-xl shadow outline-1 outline outline-n-container bg-n-solid-2 px-6 py-5"
  >
    <h3 class="mb-4 text-sm font-medium text-n-slate-11">
      {{ t('CEO_DASHBOARD.ESCALATION.HEADER') }}
    </h3>
    <div v-if="loading" class="flex items-center justify-center py-8">
      <Spinner />
    </div>
    <div v-else class="grid grid-cols-2 gap-6">
      <!-- Handoffs to Admin -->
      <div>
        <h4 class="flex items-center m-0 text-sm font-medium text-n-slate-11">
          <span>{{ t('CEO_DASHBOARD.ESCALATION.HANDOFFS') }}</span>
          <fluent-icon
            v-tooltip="t('CEO_DASHBOARD.ESCALATION.HANDOFFS_TOOLTIP')"
            size="14"
            icon="info"
            class="text-n-slate-11 my-0 mx-1 mt-0.5"
          />
        </h4>
        <h4 class="mt-1 mb-0 text-2xl font-semibold text-n-slate-12">
          {{ handoffCount.toLocaleString() }}
        </h4>
      </div>

      <!-- Unresponded by Admin -->
      <div>
        <h4 class="flex items-center m-0 text-sm font-medium text-n-slate-11">
          <span>{{ t('CEO_DASHBOARD.ESCALATION.UNRESPONDED') }}</span>
          <fluent-icon
            v-tooltip="t('CEO_DASHBOARD.ESCALATION.UNRESPONDED_TOOLTIP')"
            size="14"
            icon="info"
            class="text-n-slate-11 my-0 mx-1 mt-0.5"
          />
        </h4>
        <h4
          class="mt-1 mb-0 text-2xl font-semibold"
          :class="
            unrespondedData.count > 0 ? 'text-n-ruby-11' : 'text-n-slate-12'
          "
        >
          {{ unrespondedData.count.toLocaleString() }}
        </h4>

        <!-- Mini conversation list -->
        <div
          v-if="unrespondedData.conversations.length > 0"
          class="mt-3 flex flex-col gap-1 max-h-32 overflow-y-auto"
        >
          <router-link
            v-for="conv in unrespondedData.conversations"
            :key="conv.id"
            :to="conversationUrl(conv.id)"
            class="inline-flex items-center gap-1 text-xs text-n-blue-11 hover:underline"
          >
            <span>#{{ conv.id }}</span>
            <span class="text-n-slate-10">{{ conv.status }}</span>
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>
